import type { MaybeRefOrGetter } from "vue";

import { useHead, useHeadSafe, useSeoMeta } from "#app";
import { omit } from "radash";
import { computed, toValue } from "vue";

import type { SEOPro } from "@solvari/statamic";

import { localeToDocument, localeToIso } from "@solvari/translations";
import { getEnv } from "@solvari/utils";

import { useCurrentUrl, useHostLocale } from "~/shared/composables/useHost.ts";

// Solvari logo padded to the 1200×630 social preview size.
const fallbackOgImage =
  "https://res.cloudinary.com/solvari/image/upload/c_pad,b_white,w_1200,h_630/v1679385500/GRAPHIC_DESIGN/Images/Solvari_Thumbnail.png";

/**
 * Same as useSeoMeta, but sets the og meta tags to the same values as the regular meta tags.
 */
function useSeoNormalized(
  input: Omit<
    Parameters<typeof useSeoMeta>[0],
    "ogDescription" | "ogTitle" | "ogUpdatedTime" | "ogUrl"
  > & {
    canonical?: MaybeRefOrGetter<string | undefined>;
    follow?: MaybeRefOrGetter<boolean>;
    index?: MaybeRefOrGetter<boolean>;
  },
) {
  useSeoMeta({
    ...omit(input, ["index", "follow", "canonical"]),
    ogUrl: input.canonical,
    ogTitle: input.title as string,
    ogDescription: input.description,
    robots: () => {
      const index = toValue(input.index ?? true);
      const follow = toValue(input.follow ?? true);
      return {
        index,
        noindex: !index,
        follow,
        nofollow: !follow,
        maxImagePreview: "large",
      };
    },
  });

  useHead({
    link: [
      () =>
        toValue(input.canonical)
          ? {
              rel: "canonical",
              href: toValue(input.canonical),
              key: "canonical",
            }
          : undefined,
    ],
  });
}

function useBaseMeta() {
  const currentUrl = useCurrentUrl();

  useSeoNormalized({
    title: "Solvari",
    description: "Solvari",
    creator: "Solvari",
    ogSiteName: "Solvari",
    index: true,
    ogType: "website",
    ogImage: fallbackOgImage,
    canonical: currentUrl,
  });

  const locale = getEnv().config.localeCode;
  useSeoMeta({ ogLocale: localeToIso(locale).replace("-", "_") });
  useHeadSafe({ htmlAttrs: { lang: localeToDocument(locale) } });
}

function useStructuredData(
  data: MaybeRefOrGetter<Record<string, unknown> | undefined>,
) {
  useHead(() => {
    if (!toValue(data)) {
      return;
    }
    return {
      script: [
        {
          type: "application/ld+json",
          innerHTML: computed(() => ({
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            "@context": "https://schema.org",
            ...toValue(data),
          })),
        },
      ],
    };
  });
}

function useWebPageStructuredData(
  seoProData: MaybeRefOrGetter<SEOPro | undefined>,
) {
  const locale = useHostLocale();
  const currentUrl = useCurrentUrl();

  useStructuredData(() => {
    const seo = toValue(seoProData);
    if (!seo) {
      return undefined;
    }
    return {
      /* eslint-disable @typescript-eslint/naming-convention */
      "@type": "WebPage",
      name: seo.compiledTitle ?? undefined,
      url: currentUrl(),
      description: seo.description ?? undefined,
      inLanguage: locale,
      dateModified: seo.lastModified ?? undefined,
      /* eslint-enable @typescript-eslint/naming-convention */
    };
  });
}

export {
  fallbackOgImage,
  useBaseMeta,
  useSeoNormalized,
  useStructuredData,
  useWebPageStructuredData,
};
